/**
 * DL-01 — where the files land and the `manifest.json` written beside them.
 * PURE.
 *
 *   <picked>/<folderName>/<original file name>      the images, byte-identical
 *   <picked>/<folderName>/manifest.json              ids, md5s, the scope ids
 *
 * `folderName` is built SERVER-SIDE per decision 5 (`{date} {park} {turbine}
 * {DA UUID}`); the client only makes it a legal directory name. The file
 * names are the originals — never altered (story §B).
 */
import type {
  DownloadManifest,
  DownloadScope,
  ManifestImage,
  ManifestSkipped,
} from "./manifestApi.js";

export const MANIFEST_FILE = "manifest.json";

/**
 * A served name as ONE path segment, written AS GIVEN (the server guarantees
 * filesystem-safe names; the CLI `aiq download` writes the same names — the
 * two layouts must match byte for byte). The client only strips path
 * separators and rejects `.` / `..` / an empty name (→ `fallback`); nothing
 * is re-formatted.
 */
export function safeSegment(
  name: string,
  fallback = "data-acquisition",
): string {
  const cleaned = name.replace(/[/\\]/g, "");
  if (cleaned === "" || cleaned === "." || cleaned === "..") return fallback;
  return cleaned;
}

/** "Downloads › 2026_03_14 Nordsee WTG-07 3f2a… › DJI_0001.JPG" — for the words. */
export function displayPath(
  rootName: string,
  folderName: string,
  fileName?: string,
): string {
  return [
    rootName,
    safeSegment(folderName),
    ...(fileName ? [fileName] : []),
  ].join(" › ");
}

export interface FolderManifestImage {
  imageId: string;
  fileName: string;
  bytes: number;
  md5: string | null;
}

export interface FolderManifestJson {
  /** This file's own shape version. */
  version: 1;
  writtenAt: string;
  /** What was asked for (the DA, the instruction or the project). */
  scope: DownloadScope;
  dataAcquisitionId: string;
  inspectionInstructionId: string | null;
  projectId: string | null;
  folderName: string;
  imageCount: number;
  totalBytes: number;
  images: FolderManifestImage[];
  skipped: ManifestSkipped[];
}

/** The manifest.json of ONE folder — ids and md5s, never a URL (they expire). */
export function buildFolderManifest(
  scope: DownloadScope,
  m: DownloadManifest,
  writtenAt: Date,
): FolderManifestJson {
  return {
    version: 1,
    writtenAt: writtenAt.toISOString(),
    scope: { kind: scope.kind, id: scope.id },
    dataAcquisitionId: m.dataAcquisitionId,
    inspectionInstructionId: m.inspectionInstructionId,
    projectId: m.projectId,
    folderName: safeSegment(m.folderName),
    imageCount: m.images.length,
    totalBytes: m.images.reduce((n, i) => n + i.bytes, 0),
    images: m.images.map(
      (i: ManifestImage): FolderManifestImage => ({
        imageId: i.imageId,
        fileName: i.fileName,
        bytes: i.bytes,
        md5: i.md5,
      }),
    ),
    skipped: m.skipped,
  };
}

export function serializeFolderManifest(m: FolderManifestJson): string {
  return `${JSON.stringify(m, null, 2)}\n`;
}
